import {
  Avatar,
  AvatarFallback,
  Combobox,
  ComboboxOptions,
  Icon,
  SR_ICONS,
  useCombobox,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { queryOptionsGetWorkspacesPaginated } from '../api/workspaces/workspaceApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { isInternalUser, userInitials } from '../utils/helpers/common';
import { AppDropdownButton } from './AppDropdownButton';

/** On the accessible name, not `title`: `disabled` means no hover, so no tooltip. */
const LOCKED_REASON = '. Switching is disabled in Customer View';

export const WorkspaceSelectorMSR3 = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [activeWorkspace] = useActiveWorkspace();
  const { data: workspaces } = useQuery(queryOptionsGetWorkspacesPaginated());
  const { currentUser } = useCurrentUserAuth();

  // Keyed on identity, not `?sr_staff=1` -- that param is absent for staff
  // sessions that did not arrive through a Service Central link.
  const isLocked = isInternalUser(currentUser);

  const numberOfWorkspaces = workspaces?.items?.length ?? 0;

  const options = useMemo((): ComboboxOptions => {
    if (!workspaces?.items) return [];

    return workspaces.items.map((workspace) => ({
      element: (
        <div className="flex items-center gap-3">
          <Avatar variant="square">
            <AvatarFallback className="bg-black text-sm text-white">
              {userInitials(workspace.workspaceName)}
            </AvatarFallback>
          </Avatar>
          <div className="text-sm text-gray-900">{workspace.workspaceName}</div>
        </div>
      ),
      label: workspace.workspaceName,
      onSelect: () => {
        // The disabled trigger already makes this unreachable; belt-and-braces
        // so the lock does not rest on the trigger's markup alone.
        if (isLocked) return;

        navigate(`/workspace/${workspace._id}/home`);
        setIsOpen(false);
      },
      value: workspace._id,
    }));
  }, [workspaces, isLocked, navigate]);

  const comboboxStore = useCombobox({
    allowDeselect: false,
    hasSearch: numberOfWorkspaces > 5,
    isMultiselect: false,
    options,
    selected: [],
    showCheck: false,
    sorting: 'none',
  });

  return (
    <Combobox
      popoverProps={{
        onOpenChange: setIsOpen,
        open: isOpen,
      }}
      store={comboboxStore}
      trigger={() => (
        <AppDropdownButton
          aria-label={`Selected workspace: ${activeWorkspace?.workspaceName}${
            isLocked ? LOCKED_REASON : ''
          }`}
          // `disabled:[&>svg]:` would compile to `>svg:disabled` and silently
          // never match; opacity-100 keeps the name above WCAG AA on white.
          className="w-full disabled:opacity-100 [&:disabled>svg]:text-muted-foreground"
          disabled={isLocked}
          overrideClass={false}
          // Spread: props land last, so an explicit `undefined` would wipe out
          // the default chevron for everyone else.
          {...(isLocked && {
            sectionRight: <Icon icon={SR_ICONS.lock} weight="bold" />,
          })}
        >
          <AppDropdownButton.Avatar>
            {userInitials(activeWorkspace?.workspaceName ?? '')}
          </AppDropdownButton.Avatar>

          <AppDropdownButton.Text>
            {activeWorkspace?.workspaceName}
          </AppDropdownButton.Text>
        </AppDropdownButton>
      )}
    />
  );
};
